import React, { useEffect } from "react";
import Add from "@mui/icons-material/Add";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Slide from "@mui/material/Slide";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { background, CircularProgressSize } from "../theme";
import DeleteForever from "@mui/icons-material/DeleteForever";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import { useWindowSize } from "usehooks-ts";
import Stack from "@mui/material/Stack";

const Transition = React.forwardRef(function Transition(props, ref) {
  // @ts-expect-error - Slide is a valid component
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * @param {object} props
 * @param {string} props.title
 * @param {string} [props.subtitle]
 * @param {string} [props.id]
 * @param {string} [props.actionText]
 * @param {Function} [props.onSaveHandler]
 * @param {Function} [props.onDeleteHandler]
 * @param {boolean} [props.canSave]
 * @param {boolean} [props.isBusy]
 * @param {import("react").ReactNode} [props.children]
 * @param {import("react").ReactNode} [props.CustomControl]
 * @param {"flex-end"|"center"|"flex-start"} [props.iconAlign]
 * @param {(() => React.JSX.Element)|import("@mui/material/OverridableComponent").OverridableComponent<import("@mui/material").SvgIconTypeMap<{}, "svg">> & { muiName: string; }} [props.Icon]
 * @param {boolean} [props.textButton]
 * @param {boolean} [props.disabled]
 * @param {"small"|"medium"|"large"} [props.size]
 * @param {import("react").ReactNode} [props.DeleteIcon]
 * @param {string} [props.minWidth]
 * @param {string|undefined} [props.tooltip]
 * @param {boolean} [props.forceClose]
 * @param {boolean} [props.fullscreen]
 * @param {() => void} [props.onOpen]
 * @param {boolean} [props.open] When provided (including `false`), the dialog is controlled and no open trigger is rendered.
 * @param {() => void} [props.onClose] Called when the user closes a controlled dialog (toolbar close, backdrop, escape).
 */
export const FullScreenDialog = ({
  id = "add",
  isBusy = false,
  title,
  subtitle,
  actionText,
  onSaveHandler,
  onDeleteHandler,
  canSave,
  children,
  iconAlign = "flex-end",
  Icon = () => <Add />,
  textButton = false,
  disabled = false,
  size = "medium",
  tooltip = undefined,
  CustomControl = null,
  DeleteIcon = <DeleteForever />,
  minWidth = "50%",
  forceClose = false,
  fullscreen = false,
  onOpen,
  open: openProp,
  onClose: onCloseProp,
}) => {
  const isControlled = openProp !== undefined;
  const [internalOpen, setInternalOpen] = React.useState(false);
  const open = isControlled ? openProp : internalOpen;
  const { height } = useWindowSize();

  const handleClickOpen = () => {
    if (disabled) {
      return;
    }
    if (onOpen) {
      onOpen();
    }
    if (!isControlled) {
      setInternalOpen(true);
    }
  };

  const handleClose = () => {
    if (isControlled) {
      onCloseProp?.();
    } else {
      setInternalOpen(false);
    }
  };

  const handleAction = async (ev) => {
    ev.preventDefault();

    if (onSaveHandler) {
      await onSaveHandler();
    }

    handleClose();
  };

  const handleDelete = async (ev) => {
    ev.preventDefault();

    if (onDeleteHandler) {
      await onDeleteHandler();
    }

    handleClose();
  };

  useEffect(() => {
    if (forceClose) {
      handleClose();
    }
  }, [forceClose]);

  return (
    <>
      {!isControlled && CustomControl && <Box onClick={handleClickOpen}>{CustomControl}</Box>}
      {!isControlled && textButton && !CustomControl && (
        <Link onClick={handleClickOpen} sx={{ cursor: "pointer" }}>
          <Typography variant="body2" component="span">
            edit
          </Typography>
        </Link>
      )}
      {!isControlled && !textButton && !CustomControl && (
        <Box justifyContent={iconAlign}>
          <Tooltip title={tooltip}>
            <IconButton color="primary" id={id} onClick={handleClickOpen} size={size} disabled={disabled}>
              <Icon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Dialog
        id={`${id}-dialog`}
        open={open}
        onClose={handleClose}
        fullScreen={fullscreen}
        TransitionComponent={Transition}
        scroll="paper"
        PaperProps={{
          sx: {
            backgroundColor: background,
            minWidth: { xs: "95%", md: minWidth },
            width: { xs: "100%" },
            maxWidth: "960px",
          },
        }}
      >
        <Box id="modal">
          <DialogTitle>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={handleClose} aria-label="close">
                <CloseIcon />
              </IconButton>
              <Stack direction="column" sx={{ ml: 2, flex: 1 }}>
                <Typography variant="h6" component="div">
                  {title}
                </Typography>
                {subtitle && (
                  <Typography variant="body2" component="div">
                    {subtitle}
                  </Typography>
                )}
              </Stack>
              {onDeleteHandler && (
                <IconButton color="inherit" onClick={handleDelete} aria-label="delete">
                  {DeleteIcon}
                </IconButton>
              )}
              {actionText && (
                <Button id="dialog-save" variant="outlined" onClick={handleAction} disabled={canSave} color="inherit">
                  {isBusy ? <CircularProgress size={CircularProgressSize} /> : actionText}
                </Button>
              )}
            </Toolbar>
          </DialogTitle>
          <DialogContent dividers>
            {/* <DialogContentText> */}
            <Paper>
              <Box maxHeight={height * 0.8} overflow="scroll" sx={{ p: { xs: 1, md: 2 } }}>
                {children}
              </Box>
            </Paper>
            {/* </DialogContentText> */}
          </DialogContent>
        </Box>
      </Dialog>
    </>
  );
};
